import React, { useState, useEffect } from "react";
import { FaTrash } from "react-icons/fa";
import { auth } from "../../firebase";
import AdminSidebar from "../../components/admin/AdminSidebar";
import AdminHeader from "../../components/admin/AdminHeader";
import projectService from "../../services/projectService";
import userService from "../../services/userService";
import "../../styles/admin/ManageProjects.css";
import CustomModal from "../../components/CustomModal";

function ManageProjects() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProject, setNewProject] = useState({
    title: "",
    description: "",
    budget: "",
    deadline: "",
    skills: "",
    image: ""
  });

  // Modal State
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: null,
    confirmText: "OK",
    cancelText: "Cancel"
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectsData, usersData] = await Promise.all([
        projectService.fetchAllProjects(),
        userService.getAllUsers()
      ]);

      if (projectsData) {
        setProjects(projectsData);
      }
      if (usersData) {
        setUsers(usersData.filter(u => u.role === 'freelancer'));
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const showModal = (title, message, type = 'info', onConfirm = null, confirmText = 'OK', cancelText = 'Cancel') => {
    setModalState({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: onConfirm ? () => { onConfirm(); setModalState(prev => ({ ...prev, isOpen: false })); } : null,
      confirmText,
      cancelText
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();

    if (!newProject.title || !newProject.description) {
      showModal("Input Required", "Please fill in title and description", "info");
      return;
    }

    try {
      await projectService.createProject({
        title: newProject.title,
        description: newProject.description,
        budget: Number(newProject.budget) || 0,
        deadline: newProject.deadline,
        skills: newProject.skills.split(',').map(s => s.trim()).filter(s => s),
        image: newProject.image,
        createdBy: auth.currentUser?.uid || "admin"
      });

      showModal("Success", "Project created successfully!", "success");
      setNewProject({ title: "", description: "", budget: "", deadline: "", skills: "", image: "" });
      setShowCreateForm(false);
      loadData();
    } catch (error) {
      console.error("Failed to create project", error);
      showModal("Error", "Failed to create project.", "error");
    }
  };

  const handleAssign = async (projectId, freelancerId) => {
    if (!freelancerId) return;

    let freelancerName = "";

    if (freelancerId === "ALL") {
      freelancerName = "All Users";
    } else {
      const freelancer = users.find(u => u._id === freelancerId);
      if (!freelancer) return;
      freelancerName = freelancer.name;
    }

    try {
      await projectService.assignFreelancer(projectId, freelancerId, freelancerName);
      showModal("Success", "Project assigned successfully!", "success");
      loadData();
    } catch (error) {
      console.error("Failed to assign project", error);
      showModal("Error", "Failed to assign project.", "error");
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await projectService.updateProjectStatus(projectId, newStatus);
      showModal("Success", "Status updated successfully!", "success");
      loadData();
    } catch (error) {
      console.error("Failed to update status", error);
      showModal("Error", "Failed to update status.", "error");
    }
  };

  const handleDeleteProject = (projectId) => {
    showModal(
      "Delete Project",
      "Are you sure you want to delete this project? This action cannot be undone.",
      "danger",
      () => confirmDeleteProject(projectId),
      "Delete",
      "Cancel"
    );
  };

  const confirmDeleteProject = async (projectId) => {
    try {
      await projectService.deleteProject(projectId);
      showModal("Success", "Project deleted successfully!", "success");
      loadData();
    } catch (error) {
      console.error("Failed to delete project", error);
      showModal("Error", "Failed to delete project.", "error");
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <div className="admin-content">
        <AdminHeader title="Manage Projects" />
        <div className="page-header" style={{ padding: '0 2rem', marginTop: '1rem' }}>
          <button
            className="btn-create"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? "Cancel" : "+ Create Project"}
          </button>
        </div>

        {showCreateForm && (
          <div className="create-project-form" style={{ margin: '1rem 2rem' }}>
            <h3>Create New Project</h3>
            <form onSubmit={handleCreateProject}>
              <div className="form-group">
                <label>Project Title *</label>
                <input
                  type="text"
                  value={newProject.title}
                  onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                  placeholder="Enter project title"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  placeholder="Enter project description"
                  rows="4"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Budget (₹)</label>
                  <input
                    type="number"
                    value={newProject.budget}
                    onChange={(e) => setNewProject({ ...newProject, budget: e.target.value })}
                    placeholder="Enter budget"
                  />
                </div>
                <div className="form-group">
                  <label>Deadline</label>
                  <input
                    type="date"
                    value={newProject.deadline}
                    onChange={(e) => setNewProject({ ...newProject, deadline: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Skills (comma separated)</label>
                <input
                  type="text"
                  value={newProject.skills}
                  onChange={(e) => setNewProject({ ...newProject, skills: e.target.value })}
                  placeholder="e.g. React, Node.js, Python"
                />
              </div>
              <div className="form-group">
                <label>Cover Image URL (Optional)</label>
                <input
                  type="text"
                  value={newProject.image}
                  onChange={(e) => setNewProject({ ...newProject, image: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
              <button type="submit" className="btn-primary">Create Project</button>
            </form>
          </div>
        )
        }

        {
          loading ? (
            <p style={{ padding: '0 2rem' }}>Loading projects...</p>
          ) : (
            <div className="projects-table-wrapper" style={{ padding: '0 2rem' }}>
              <table className="projects-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Budget</th>
                    <th>Deadline</th>
                    <th>Assigned To</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center' }}>No projects yet</td>
                    </tr>
                  ) : (
                    projects.map((project) => (
                      <React.Fragment key={project._id}>
                        <tr>
                          <td>{project.title}</td>
                          <td className="description-cell">{project.description}</td>
                          <td>₹{project.budget?.toLocaleString() || 0}</td>
                          <td>{project.deadline || "Not set"}</td>
                          <td>{project.assignedToName || "Unassigned"}</td>
                          <td>
                            <select
                              value={project.status}
                              onChange={(e) => handleStatusChange(project._id, e.target.value)}
                              className="status-select"
                            >
                              <option value="Open">Open</option>
                              <option value="Assigned">Assigned</option>
                              <option value="In Progress">In Progress</option>
                              <option value="QA">QA</option>
                              <option value="Completed">Completed</option>
                            </select>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              <select
                                onChange={(e) => handleAssign(project._id, e.target.value)}
                                defaultValue=""
                                className="assign-select"
                              >
                                <option value="" disabled>Assign to...</option>
                                <option value="ALL" style={{ fontWeight: 'bold', color: '#1a73e8' }}>Select All Users</option>
                                {users.map(user => (
                                  <option key={user._id} value={user._id}>
                                    {user.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => handleDeleteProject(project._id)}
                                className="btn-icon delete"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ff4d4f',
                                  cursor: 'pointer',
                                  padding: '0.5rem'
                                }}
                                title="Delete Project"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
      <CustomModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        onConfirm={modalState.onConfirm}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
      />
    </div>
  );
}

export default ManageProjects;
